import axios from 'axios';
import * as XLSX from 'xlsx';
import { v4 as uuidv4 } from 'uuid';
import { supabase, checkSupabaseHealth } from './supabaseClient';

const FREE_DAILY_NEW_FLASHCARDS_CAP = 10;

const getSessionId = () => {
  const newKey = 'lexory_session_id';
  const legacyKey = ['t', 'r', 'a', 'd', 'u', 'x'].join('') + '_session_id';

  let sessionId = localStorage.getItem(newKey) || localStorage.getItem(legacyKey);
  if (!sessionId) {
    sessionId = uuidv4();
  }

  localStorage.setItem(newKey, sessionId);
  return sessionId;
};

const getAuthHeaderValue = async () => {
  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) return null;
    const token = data?.session?.access_token;
    if (!token) return null;
    return `Bearer ${token}`;
  } catch {
    return null;
  }
};

export const resolveTierForCurrentUser = async () => {
  try {
    const sessionId = getSessionId();
    const authHeader = await getAuthHeaderValue();

    const headers = {
      'X-Session-Id': sessionId,
    };

    if (authHeader) {
      headers.Authorization = authHeader;
    }

    const response = await axios.get('/api/usage', { headers });
    const tier = response?.data?.tier;
    return typeof tier === 'string' && tier ? tier : 'free';
  } catch {
    return 'free';
  }
};

const startOfUtcDayIso = (date = new Date()) => {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0));
  return d.toISOString();
};

const makeCodedError = (code, message, extras = {}) => {
  const err = new Error(message);
  err.code = code;
  Object.assign(err, extras);
  return err;
};

const requireUser = (user = null) => {
  if (!user) {
    throw new Error('Authentication required');
  }
};

export const listDecks = async (user = null) => {
  try {
    const isHealthy = await checkSupabaseHealth();
    if (!isHealthy) {
      throw new Error('Database connection unavailable');
    }

    requireUser(user);

    const { data, error } = await supabase
      .from('flashcard_decks')
      .select('*')
      .eq('user_id', user.id)
      .order('updated_at', { ascending: false });

    if (error) {
      throw error;
    }

    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error listing decks:', error);
    return { data: [], error: error.message };
  }
};

export const getDeck = async (user = null, deckId) => {
  try {
    const isHealthy = await checkSupabaseHealth();
    if (!isHealthy) throw new Error('Database connection unavailable');
    requireUser(user);

    const { data, error } = await supabase
      .from('flashcard_decks')
      .select('*')
      .eq('id', deckId)
      .eq('user_id', user.id)
      .maybeSingle();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error getting deck:', error);
    return { data: null, error: error.message };
  }
};

export const createDeck = async (user = null, { name, description = null, front_language = null, back_language = null } = {}) => {
  try {
    const isHealthy = await checkSupabaseHealth();
    if (!isHealthy) {
      throw new Error('Database connection unavailable');
    }

    requireUser(user);

    const cleanName = typeof name === 'string' ? name.trim() : '';
    if (!cleanName) {
      throw new Error('Deck name is required');
    }

    const payload = {
      user_id: user.id,
      name: cleanName,
      description: typeof description === 'string' && description.trim() ? description.trim() : null,
      front_language: typeof front_language === 'string' && front_language.trim() ? front_language.trim() : null,
      back_language: typeof back_language === 'string' && back_language.trim() ? back_language.trim() : null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from('flashcard_decks')
      .insert([payload])
      .select()
      .single();

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error creating deck:', error);
    return { data: null, error: error.message };
  }
};

export const renameDeck = async (user = null, deckId, { name, description, front_language, back_language } = {}) => {
  try {
    const isHealthy = await checkSupabaseHealth();
    if (!isHealthy) {
      throw new Error('Database connection unavailable');
    }

    requireUser(user);

    const cleanName = typeof name === 'string' ? name.trim() : '';
    if (!cleanName) {
      throw new Error('Deck name is required');
    }

    const payload = {
      name: cleanName,
      updated_at: new Date().toISOString(),
    };

    if (description !== undefined) {
      payload.description = typeof description === 'string' && description.trim() ? description.trim() : null;
    }
    if (front_language !== undefined) {
      payload.front_language = typeof front_language === 'string' && front_language.trim() ? front_language.trim() : null;
    }
    if (back_language !== undefined) {
      payload.back_language = typeof back_language === 'string' && back_language.trim() ? back_language.trim() : null;
    }

    const { data, error } = await supabase
      .from('flashcard_decks')
      .update(payload)
      .eq('id', deckId)
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error renaming deck:', error);
    return { data: null, error: error.message };
  }
};

export const deleteDeck = async (user = null, deckId) => {
  try {
    const isHealthy = await checkSupabaseHealth();
    if (!isHealthy) {
      throw new Error('Database connection unavailable');
    }

    requireUser(user);

    const { error } = await supabase
      .from('flashcard_decks')
      .delete()
      .eq('id', deckId)
      .eq('user_id', user.id);

    if (error) {
      throw error;
    }

    return { error: null };
  } catch (error) {
    console.error('Error deleting deck:', error);
    return { error: error.message };
  }
};

export const listCards = async (user = null, filters = {}) => {
  try {
    const isHealthy = await checkSupabaseHealth();
    if (!isHealthy) {
      throw new Error('Database connection unavailable');
    }

    requireUser(user);

    let query = supabase
      .from('flashcards')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (filters.deckId) {
      query = query.eq('deck_id', filters.deckId);
    }

    if (filters.searchText) {
      const q = String(filters.searchText).trim();
      if (q) {
        query = query.or(`front_text.ilike.%${q}%,back_text.ilike.%${q}%`);
      }
    }

    const { data, error } = await query;

    if (error) {
      throw error;
    }

    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error listing cards:', error);
    return { data: [], error: error.message };
  }
};

const getTodaysNewCardCount = async (userId) => {
  const since = startOfUtcDayIso(new Date());
  const { count, error } = await supabase
    .from('flashcards')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .gte('created_at', since);

  if (error) {
    throw error;
  }

  return typeof count === 'number' ? count : 0;
};

const enforceDailyCapOrThrow = async (userId) => {
  const tier = await resolveTierForCurrentUser();
  if (tier === 'premium') {
    return { tier, remaining: null, limit: null, used: null };
  }

  const used = await getTodaysNewCardCount(userId);
  const limit = FREE_DAILY_NEW_FLASHCARDS_CAP;
  const remaining = Math.max(0, limit - used);

  if (remaining <= 0) {
    throw makeCodedError(
      'FLASHCARDS_DAILY_CAP_REACHED',
      `Free plan allows ${limit} new flashcards per day. Upgrade to create unlimited cards.`,
      { tier, limit, used, remaining }
    );
  }

  return { tier, limit, used, remaining };
};

export const createCard = async (user = null, payload = {}) => {
  try {
    const isHealthy = await checkSupabaseHealth();
    if (!isHealthy) {
      throw new Error('Database connection unavailable');
    }

    requireUser(user);

    await enforceDailyCapOrThrow(user.id);

    const cleanFront = typeof payload.front_text === 'string' ? payload.front_text.trim() : '';
    const cleanBack = typeof payload.back_text === 'string' ? payload.back_text.trim() : '';

    if (!payload.deck_id) {
      throw new Error('Deck is required');
    }
    if (!cleanFront) {
      throw new Error('Front text is required');
    }

    const row = {
      user_id: user.id,
      deck_id: payload.deck_id,
      front_text: cleanFront,
      back_text: cleanBack || null,
      front_image_url: typeof payload.front_image_url === 'string' && payload.front_image_url.trim() ? payload.front_image_url.trim() : null,
      back_image_url: typeof payload.back_image_url === 'string' && payload.back_image_url.trim() ? payload.back_image_url.trim() : null,
      tags: Array.isArray(payload.tags) ? payload.tags.filter(Boolean).map(String) : [],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from('flashcards')
      .insert([row])
      .select()
      .single();

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error creating card:', error);
    return { data: null, error: error.message, code: error?.code };
  }
};

export const updateCard = async (user = null, cardId, updates = {}) => {
  try {
    const isHealthy = await checkSupabaseHealth();
    if (!isHealthy) {
      throw new Error('Database connection unavailable');
    }

    requireUser(user);

    const payload = {
      updated_at: new Date().toISOString(),
    };

    if (updates.deck_id !== undefined) payload.deck_id = updates.deck_id;
    if (updates.front_text !== undefined) payload.front_text = String(updates.front_text).trim();
    if (updates.back_text !== undefined) payload.back_text = String(updates.back_text).trim() || null;
    if (updates.front_image_url !== undefined) payload.front_image_url = String(updates.front_image_url).trim() || null;
    if (updates.back_image_url !== undefined) payload.back_image_url = String(updates.back_image_url).trim() || null;
    if (updates.tags !== undefined) payload.tags = Array.isArray(updates.tags) ? updates.tags.filter(Boolean).map(String) : [];

    const { data, error } = await supabase
      .from('flashcards')
      .update(payload)
      .eq('id', cardId)
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error updating card:', error);
    return { data: null, error: error.message };
  }
};

export const deleteCard = async (user = null, cardId) => {
  try {
    const isHealthy = await checkSupabaseHealth();
    if (!isHealthy) {
      throw new Error('Database connection unavailable');
    }

    requireUser(user);

    const { error } = await supabase
      .from('flashcards')
      .delete()
      .eq('id', cardId)
      .eq('user_id', user.id);

    if (error) {
      throw error;
    }

    return { error: null };
  } catch (error) {
    console.error('Error deleting card:', error);
    return { error: error.message };
  }
};

export const bulkDeleteCards = async (user = null, cardIds = []) => {
  try {
    const isHealthy = await checkSupabaseHealth();
    if (!isHealthy) {
      throw new Error('Database connection unavailable');
    }

    requireUser(user);

    const ids = Array.isArray(cardIds) ? cardIds.filter(Boolean) : [];
    if (ids.length === 0) {
      return { error: null };
    }

    const { error } = await supabase
      .from('flashcards')
      .delete()
      .eq('user_id', user.id)
      .in('id', ids);

    if (error) {
      throw error;
    }

    return { error: null };
  } catch (error) {
    console.error('Error bulk deleting cards:', error);
    return { error: error.message };
  }
};

export const parseImportFile = async (file) => {
  const name = file?.name ? String(file.name) : '';
  const ext = name.toLowerCase().split('.').pop();
  if (!file || !ext) {
    throw new Error('Invalid file');
  }

  const buf = await file.arrayBuffer();
  let wb;
  if (ext === 'csv') {
    const text = new TextDecoder().decode(buf);
    wb = XLSX.read(text, { type: 'string' });
  } else {
    wb = XLSX.read(buf, { type: 'array' });
  }
  const firstSheetName = wb.SheetNames?.[0];
  if (!firstSheetName) {
    throw new Error('No sheets found');
  }

  const ws = wb.Sheets[firstSheetName];
  const matrix = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

  const headers = Array.isArray(matrix?.[0]) ? matrix[0].map((h) => String(h ?? '').trim()) : [];
  const rows = Array.isArray(matrix) ? matrix.slice(1) : [];

  return { headers, rows, ext };
};

const resolveColIndex = (headers, col) => {
  if (typeof col === 'number' && Number.isFinite(col) && col >= 0) return col;
  if (typeof col === 'string') {
    const idx = headers.findIndex((h) => String(h).trim() === col.trim());
    if (idx >= 0) return idx;
  }
  return -1;
};

export const normalizeImportRows = ({ headers, rows }, mapping = {}) => {
  const frontIdx = resolveColIndex(headers, mapping.front);
  const backIdx = resolveColIndex(headers, mapping.back);
  const frontImgIdx = resolveColIndex(headers, mapping.frontImageUrl);
  const backImgIdx = resolveColIndex(headers, mapping.backImageUrl);
  const tagsIdx = resolveColIndex(headers, mapping.tags);

  if (frontIdx < 0) {
    throw new Error('Front column mapping is required');
  }

  return (Array.isArray(rows) ? rows : [])
    .map((r) => {
      const row = Array.isArray(r) ? r : [];
      const frontText = String(row[frontIdx] ?? '').trim();
      const backText = backIdx >= 0 ? String(row[backIdx] ?? '').trim() : '';
      const frontImageUrl = frontImgIdx >= 0 ? String(row[frontImgIdx] ?? '').trim() : '';
      const backImageUrl = backImgIdx >= 0 ? String(row[backImgIdx] ?? '').trim() : '';
      const rawTags = tagsIdx >= 0 ? String(row[tagsIdx] ?? '').trim() : '';

      const tags = rawTags
        ? rawTags
            .split(/[;,]/g)
            .map((t) => t.trim())
            .filter(Boolean)
        : [];

      if (!frontText) return null;

      return {
        front_text: frontText,
        back_text: backText || null,
        front_image_url: frontImageUrl || null,
        back_image_url: backImageUrl || null,
        tags,
      };
    })
    .filter(Boolean);
};

// ---------------------------------------------------------------------------
// Spaced-repetition helpers
// ---------------------------------------------------------------------------

const INTERVALS = {
  hard:  [10 / 60 / 24, 1, 1, 1],       // 10 min, 1d, 1d, 1d
  good:  [1, 3, 7, 14],                   // 1d, 3d, 7d, 14d
  easy:  [3, 7, 14, 30],                  // 3d, 7d, 14d, 30d
};

const computeNextReview = (grade, streak) => {
  const intervals = INTERVALS[grade] || INTERVALS.good;
  const idx = Math.min(streak, intervals.length - 1);
  const days = intervals[idx];
  return new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
};

export const gradeCard = async (user = null, cardId, grade, currentCard = {}) => {
  try {
    const isHealthy = await checkSupabaseHealth();
    if (!isHealthy) throw new Error('Database connection unavailable');
    requireUser(user);

    const prevStreak = typeof currentCard.streak === 'number' ? currentCard.streak : 0;
    const prevReviewCount = typeof currentCard.review_count === 'number' ? currentCard.review_count : 0;

    let confidence, nextStreak;
    if (grade === 'hard') {
      confidence = 1;
      nextStreak = 0;
    } else if (grade === 'easy') {
      confidence = 3;
      nextStreak = prevStreak + 1;
    } else {
      confidence = 2;
      nextStreak = prevStreak + 1;
    }

    const payload = {
      confidence,
      streak: nextStreak,
      review_count: prevReviewCount + 1,
      last_reviewed: new Date().toISOString(),
      next_review: computeNextReview(grade, nextStreak),
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from('flashcards')
      .update(payload)
      .eq('id', cardId)
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error grading card:', error);
    return { data: null, error: error.message };
  }
};

export const getDueCards = async (user = null, deckId) => {
  try {
    const isHealthy = await checkSupabaseHealth();
    if (!isHealthy) throw new Error('Database connection unavailable');
    requireUser(user);

    const now = new Date().toISOString();

    // New cards (reviewed at least once but reset to confidence 0 via "hard")
    let newQuery = supabase
      .from('flashcards')
      .select('*')
      .eq('user_id', user.id)
      .eq('confidence', 0)
      .gt('review_count', 0);
    if (deckId) newQuery = newQuery.eq('deck_id', deckId);
    newQuery = newQuery.order('created_at', { ascending: true });

    // Due cards (next_review <= now)
    let dueQuery = supabase
      .from('flashcards')
      .select('*')
      .eq('user_id', user.id)
      .gt('confidence', 0)
      .lte('next_review', now);
    if (deckId) dueQuery = dueQuery.eq('deck_id', deckId);
    dueQuery = dueQuery.order('next_review', { ascending: true });

    const [newResult, dueResult] = await Promise.all([newQuery, dueQuery]);

    if (newResult.error) throw newResult.error;
    if (dueResult.error) throw dueResult.error;

    const combined = [...(newResult.data || []), ...(dueResult.data || [])];
    return { data: combined, error: null };
  } catch (error) {
    console.error('Error getting due cards:', error);
    return { data: [], error: error.message };
  }
};

export const getDeckStats = async (user = null, deckId) => {
  try {
    const isHealthy = await checkSupabaseHealth();
    if (!isHealthy) throw new Error('Database connection unavailable');
    requireUser(user);

    let query = supabase
      .from('flashcards')
      .select('confidence, next_review, review_count')
      .eq('user_id', user.id);
    if (deckId) query = query.eq('deck_id', deckId);

    const { data, error } = await query;
    if (error) throw error;

    const cards = data || [];
    const now = new Date();
    let newCount = 0;
    let dueCount = 0;
    let learnedCount = 0;

    for (const c of cards) {
      const reviewed = (c.review_count || 0) > 0;
      if (!reviewed) {
        // Untouched card — not counted in any smart-review bucket
      } else if (c.confidence === 0 || c.confidence === null) {
        newCount++;
      } else if (c.next_review && new Date(c.next_review) <= now) {
        dueCount++;
      } else {
        learnedCount++;
      }
    }

    return {
      data: { newCount, dueCount, learnedCount, totalCount: cards.length },
      error: null,
    };
  } catch (error) {
    console.error('Error getting deck stats:', error);
    return { data: { newCount: 0, dueCount: 0, learnedCount: 0, totalCount: 0 }, error: error.message };
  }
};

export const insertCardsInChunks = async (user = null, { deckId, cards, chunkSize = 150, dedupe = true } = {}) => {
  try {
    const isHealthy = await checkSupabaseHealth();
    if (!isHealthy) {
      throw new Error('Database connection unavailable');
    }

    requireUser(user);

    if (!deckId) {
      throw new Error('Deck is required');
    }

    const tier = await resolveTierForCurrentUser();
    let allowedCards = Array.isArray(cards) ? cards : [];

    let capInfo = null;
    if (tier !== 'premium') {
      const used = await getTodaysNewCardCount(user.id);
      const limit = FREE_DAILY_NEW_FLASHCARDS_CAP;
      const remaining = Math.max(0, limit - used);
      capInfo = { tier, used, limit, remaining };

      if (remaining <= 0) {
        throw makeCodedError(
          'FLASHCARDS_DAILY_CAP_REACHED',
          `Free plan allows ${limit} new flashcards per day. Upgrade to create unlimited cards.`,
          capInfo
        );
      }

      allowedCards = allowedCards.slice(0, remaining);
    }

    const nowIso = new Date().toISOString();
    const rows = allowedCards.map((c) => ({
      user_id: user.id,
      deck_id: deckId,
      front_text: String(c.front_text ?? '').trim(),
      back_text: typeof c.back_text === 'string' && c.back_text.trim() ? c.back_text.trim() : null,
      front_image_url: typeof c.front_image_url === 'string' && c.front_image_url.trim() ? c.front_image_url.trim() : null,
      back_image_url: typeof c.back_image_url === 'string' && c.back_image_url.trim() ? c.back_image_url.trim() : null,
      tags: Array.isArray(c.tags) ? c.tags.filter(Boolean).map(String) : [],
      created_at: nowIso,
      updated_at: nowIso,
    })).filter((r) => r.front_text);

    let inserted = 0;
    for (let i = 0; i < rows.length; i += chunkSize) {
      const chunk = rows.slice(i, i + chunkSize);
      const q = supabase.from('flashcards');

      const { data, error } = dedupe
        ? await q.upsert(chunk, { onConflict: 'user_id,deck_id,front_text', ignoreDuplicates: true }).select('id')
        : await q.insert(chunk).select('id');

      if (error) {
        throw error;
      }

      inserted += Array.isArray(data) ? data.length : 0;
    }

    const requested = Array.isArray(cards) ? cards.length : 0;
    const skippedDueToCap = requested - allowedCards.length;

    return {
      inserted,
      skippedDueToCap: skippedDueToCap > 0 ? skippedDueToCap : 0,
      cap: capInfo,
      error: null,
    };
  } catch (error) {
    console.error('Error inserting cards in chunks:', error);
    return {
      inserted: 0,
      skippedDueToCap: 0,
      cap: null,
      error: error.message,
      code: error?.code,
      details: error,
    };
  }
};
